import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../../context/AuthContext'
import { adminApi } from '../../utils/api'
import { Key, Plus, Pause, Play, Trash2, Copy, Check, AlertCircle, Loader2 } from 'lucide-react'

interface BetaToken {
  token_id: string
  token_prefix: string
  label: string
  publisher_id?: string
  created_by: string
  status: 'active' | 'suspended' | 'revoked'
  last_used_at?: string
  expires_at?: string
  created_at: string
}

export default function AdminBetaTokensPage() {
  const { token } = useAuth()
  const [tokens, setTokens] = useState<BetaToken[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Generation form
  const [showGenerate, setShowGenerate] = useState(false)
  const [genLabel, setGenLabel] = useState('')
  const [genCount, setGenCount] = useState(1)
  const [genExpiry, setGenExpiry] = useState<number | ''>('')
  const [generating, setGenerating] = useState(false)

  // Newly generated tokens (shown once)
  const [generatedTokens, setGeneratedTokens] = useState<{ token: string; token_id: string; label: string }[]>([])
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const loadTokens = useCallback(async () => {
    if (!token) return
    try {
      setLoading(true)
      const data = await adminApi.getBetaTokens(token)
      setTokens(data.items || [])
    } catch (err) {
      setError('Failed to load beta tokens')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }, [token])

  useEffect(() => {
    loadTokens()
  }, [loadTokens])

  const handleGenerate = async () => {
    if (!token || !genLabel.trim()) return
    setGenerating(true)
    setError(null)
    try {
      const data = await adminApi.createBetaToken(token, {
        label: genLabel.trim(),
        count: genCount,
        expires_in_days: genExpiry || undefined,
      })
      setGeneratedTokens(data.tokens)
      setShowGenerate(false)
      setGenLabel('')
      setGenCount(1)
      setGenExpiry('')
      loadTokens()
    } catch (err) {
      setError('Failed to generate tokens')
      console.error(err)
    } finally {
      setGenerating(false)
    }
  }

  const handleStatusChange = async (tokenId: string, newStatus: 'active' | 'suspended' | 'revoked') => {
    if (!token) return
    try {
      await adminApi.updateBetaToken(token, tokenId, newStatus)
      loadTokens()
    } catch (err) {
      setError('Failed to update token status')
      console.error(err)
    }
  }

  const copyToClipboard = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text)
    setCopiedId(id)
    setTimeout(() => setCopiedId(null), 2000)
  }

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800',
      suspended: 'bg-yellow-100 text-yellow-800',
      revoked: 'bg-red-100 text-red-800',
    }
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    )
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Beta Access Tokens</h1>
          <p className="text-sm text-gray-500 mt-1">Generate and manage access tokens for beta testers</p>
        </div>
        <button
          onClick={() => setShowGenerate(true)}
          className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 transition-colors"
        >
          <Plus className="w-4 h-4 mr-2" />
          Generate Tokens
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {/* Newly generated tokens (show only once) */}
      {generatedTokens.length > 0 && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 mb-3">
            <Key className="w-5 h-5 text-green-600" />
            <h3 className="text-sm font-semibold text-green-800">
              Newly Generated Tokens - Copy these now, they will not be shown again!
            </h3>
          </div>
          <div className="space-y-2">
            {generatedTokens.map((t) => (
              <div key={t.token_id} className="flex items-center gap-3 p-3 bg-white rounded border border-green-200">
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">{t.label}</p>
                  <code className="text-sm font-mono text-gray-900 break-all">{t.token}</code>
                </div>
                <button
                  onClick={() => copyToClipboard(t.token, t.token_id)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600"
                  title="Copy token"
                >
                  {copiedId === t.token_id ? (
                    <Check className="w-4 h-4 text-green-500" />
                  ) : (
                    <Copy className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => setGeneratedTokens([])}
            className="mt-3 text-sm text-green-700 hover:text-green-900 underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Generate form modal */}
      {showGenerate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Generate Beta Access Tokens</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Label</label>
                <input
                  type="text"
                  value={genLabel}
                  onChange={(e) => setGenLabel(e.target.value)}
                  placeholder="e.g., Beta Batch #1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of tokens</label>
                <input
                  type="number"
                  min={1}
                  max={50}
                  value={genCount}
                  onChange={(e) => setGenCount(Math.max(1, Math.min(50, parseInt(e.target.value) || 1)))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry (days, blank for no expiry)</label>
                <input
                  type="number"
                  min={1}
                  value={genExpiry}
                  onChange={(e) => setGenExpiry(e.target.value ? parseInt(e.target.value) : '')}
                  placeholder="No expiry"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => setShowGenerate(false)}
                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerate}
                disabled={generating || !genLabel.trim()}
                className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {generating && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tokens table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      ) : tokens.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <Key className="w-12 h-12 mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No beta tokens generated yet</p>
          <p className="text-sm text-gray-400 mt-1">Click &quot;Generate Tokens&quot; to create access tokens for beta testers</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Token</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Label</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Publisher</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Used</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expires</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tokens.map((t) => (
                <tr key={t.token_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <code className="text-xs font-mono text-gray-600">{t.token_prefix}...</code>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.label}</td>
                  <td className="px-6 py-4 whitespace-nowrap">{statusBadge(t.status)}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {t.publisher_id ? (
                      <span className="text-indigo-600">{t.publisher_id.substring(0, 8)}...</span>
                    ) : (
                      <span className="text-gray-400">Unlinked</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {t.last_used_at ? new Date(t.last_used_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {t.expires_at ? new Date(t.expires_at).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(t.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <div className="flex items-center justify-end gap-1">
                      {t.status === 'active' && (
                        <button
                          onClick={() => handleStatusChange(t.token_id, 'suspended')}
                          className="p-1.5 text-yellow-600 hover:bg-yellow-50 rounded"
                          title="Suspend token"
                        >
                          <Pause className="w-4 h-4" />
                        </button>
                      )}
                      {t.status === 'suspended' && (
                        <button
                          onClick={() => handleStatusChange(t.token_id, 'active')}
                          className="p-1.5 text-green-600 hover:bg-green-50 rounded"
                          title="Reactivate token"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                      )}
                      {t.status !== 'revoked' && (
                        <button
                          onClick={() => handleStatusChange(t.token_id, 'revoked')}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                          title="Revoke token permanently"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
